import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { Fragment, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { FlexBox, FlexBoxProps } from '../layout';

export const SortOrderFormSchema = z.object({
  optionItemsDisplayOrder: z
    .object({
      sortBy: z.enum(['NAME', 'PRICE', 'ADDED_ORDER']),
      sortDirection: z.enum(['ASC', 'DESC']),
      sortPriorityOrder: z.number().int().nonnegative(),
    })
    .array(),
});

type SortOrderFormInputs = z.infer<typeof SortOrderFormSchema>;

const sortByOptions = [
  { value: 'NAME', label: 'Name' },
  { value: 'PRICE', label: 'Price' },
  { value: 'ADDED_ORDER', label: 'Build Order' },
].sort((a, b) => a.label.localeCompare(b.label));

const getDefaultSortDirection = (sortBy: string): 'ASC' | 'DESC' => {
  switch (sortBy) {
    case 'NAME':
    case 'ADDED_ORDER':
      return 'ASC';
    case 'PRICE':
      return 'DESC';
    default:
      return 'ASC';
  }
};

const SortOrderController: React.FC<FlexBoxProps> = (props) => {
  const { control, setValue, watch } = useFormContext<SortOrderFormInputs>();

  const { fields } = useFieldArray({
    control,
    name: 'optionItemsDisplayOrder',
  });

  const availableFields = fields.slice(0, 1);

  const watchedSortBy = watch('optionItemsDisplayOrder.0.sortBy');

  useEffect(() => {
    if (watchedSortBy) {
      const defaultDirection = getDefaultSortDirection(watchedSortBy);
      setValue('optionItemsDisplayOrder.0.sortDirection', defaultDirection);
    }
  }, [watchedSortBy, setValue]);

  return (
    <FlexBox {...props}>
      <Typography variant="caption">
        Set the order in which items appear inside proposals by default. You can
        choose to sort items by build order, price, or name - and control
        whether they're shown from highest to lowest or vice versa.
      </Typography>
      {availableFields.map((option, index) => (
        <Fragment key={option.id}>
          <Typography variant="caption">
            {index === 0
              ? 'How your proposal items will be sorted by default'
              : 'Used only when multiple items have the same value for the primary field (e.g. two items with the same price)'}
          </Typography>
          <FlexBox gap={2} flexDirection={['column', 'row']}>
            <Controller
              name={`optionItemsDisplayOrder.${index}.sortBy`}
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  type="text"
                  label={`Sort`}
                  select
                  {...field}
                  error={!!fieldState.error?.message}
                  helperText={fieldState.error?.message}
                >
                  {sortByOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </FlexBox>
        </Fragment>
      ))}
    </FlexBox>
  );
};

export default SortOrderController;
